/*
 * OrderlineZufuegen.tsx
 *
 * Created by Dr. Maximillian Dornseif in huWaWi3
 * Copyright (c) 2023 HUDORA GmbH
 */

import { LadeChecker } from '@hudora/hd-react-components';
import { ValueRender } from '@hudora/react-jsonschema-valuerender';
import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CurrencyTextField from '@unicef/material-ui-currency-textfield';
import React, { useCallback, useState } from 'react';
import { gql, useQuery } from 'urql';

import { EntitySelectComboboxSearch } from './EntitySelectComboboxSearch';
import { PREIS_FRAGMENT } from '../../fragments'

/** Menge und Artikel auswählen.
 */
export function OrderlineZufuegen(props: {
  preisFeld?: string;
  onNewOrderline: (menge: number, itemNo: string, preis: number | undefined) => void;
}) {
  const [itemNr, setItemNr] = useState('');
  const [menge, setMenge] = useState('');
  const [preis, setPreis] = useState('');
  const [result] = useQuery({
    query: PREIS_QUERY,
    pause: !itemNr || itemNr.length < 5,
    variables: { nr: itemNr },
  });
  const { data, fetching } = result;

  const fhPreis = data?.h3Item?.preis?.fh;
  /* Callback mit den neu erfassten Daten */
  const orderlineSpeichern = useCallback(() => {
    props.onNewOrderline(parseInt(menge), itemNr, parseFloat(preis) * 100);
    setItemNr('');
    setMenge('');
    setPreis('');
  }, [menge, setMenge, itemNr, setItemNr, preis, setPreis, props.onNewOrderline]);

  return (
    <Stack direction="row" spacing={5}>
      <TextField
        label="Menge:"
        type="number"
        variant="standard"
        placeholder={'10'}
        value={menge}
        inputProps={{
          autoComplete: 'off',
          min: 1,
        }}
        onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setMenge((event.target as any).value || '');
        }}
      />

      <EntitySelectComboboxSearch
        schemaNames={['H3Item']}
        onChanged={setItemNr}
        label="Item№:"
        placeholder="14600"
      />

      {props.preisFeld && props.preisFeld !== '' ? (
        <CurrencyTextField
          label={props.preisFeld}
          minimumValue={'0'}
          outputFormat="string"
          variant="standard"
          decimalCharacter=","
          decimalCharacterAlternative="."
          digitGroupSeparator=" "
          currencySymbol={'\u202f€'}
          currencySymbolPlacement="s"
          value={preis}
          onChange={(event, value) => {
            setPreis(value);
          }}
          onBlur={(event, value) => {
            setPreis(value);
          }}
          // helperText=""
          // error={false}
        />
      ) : null}

      <Button variant="outlined" disabled={!fhPreis} onClick={orderlineSpeichern}>
        Zufügen
      </Button>

      <div style={{ alignItems: 'center', display: 'flex' }}>
        {itemNr.length > 4 ? (
          <LadeChecker loading={fetching}>
            {fhPreis ? (
              <React.Fragment>
                {'FH: '} <ValueRender value={fhPreis} format="eurocent" />
              </React.Fragment>
            ) : (
              <div style={{ verticalAlign: 'middle' }}>kein Preis für {itemNr} gefunden</div>
            )}
          </LadeChecker>
        ) : null}
      </div>
    </Stack>
  );
}

const PREIS_QUERY = gql`
  query PreisLesen($nr: ID!) {
    h3Item(designator: $nr) {
      id
      preis: preisResolved {
        ...preisLFragment
      }
    }
  }
  ${PREIS_FRAGMENT}
`;
